import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight, MessageCircle, FileSearch, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PRODUCTS } from "@/data/products";
import { waLink } from "@/data/content";
import { useLang, useT } from "@/i18n";

const Row = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex flex-col gap-1 border-b border-slate-800/80 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-100 sm:text-right">{value}</span>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useT();
  const product = PRODUCTS.find((p) => p.slug === slug);

  if (!product) return <Navigate to="/" replace />;

  const resolve = (v) => {
    if (v === "buyerSpec") return t.detail.buyerSpec;
    if (v === "onRequest") return t.detail.onRequest;
    return v[lang];
  };

  const infoRows = [
    { label: t.detail.origin, value: t.detail.originValue, testid: "detail-origin" },
    { label: t.detail.quality, value: resolve(product.quality), testid: "detail-quality" },
    { label: t.detail.packaging, value: resolve(product.packaging), testid: "detail-packaging" },
    { label: t.detail.moq, value: t.detail.onRequest, testid: "detail-moq" },
    { label: t.detail.capacity, value: t.detail.onRequest, testid: "detail-capacity" },
    { label: t.detail.payment, value: t.detail.paymentTerms, testid: "detail-payment" },
    { label: t.detail.shipping, value: t.detail.shippingTerms, testid: "detail-shipping" },
    { label: t.detail.documents, value: resolve(product.documents), testid: "detail-documents" },
  ];

  return (
    <main className="bg-[#0B0E14] pb-24 pt-28 lg:pt-32">
      <Helmet>
        <title>{product.seo.title}</title>
        <meta name="description" content={product.seo.desc} />
      </Helmet>

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Link
            data-testid="detail-back-link"
            to="/#products"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft size={14} /> {t.detail.back}
          </Link>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <div className="relative">
                <div className="absolute -left-3 -top-3 h-full w-full border border-gold/30" />
                <div className="clipped-frame relative aspect-[4/3] overflow-hidden">
                  <img
                    data-testid="detail-main-image"
                    src={product.image}
                    alt={product.name.en}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </Reveal>
            {product.gallery.length > 1 && (
              <Reveal delay={0.15}>
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {product.gallery.slice(1).map((src) => (
                    <div key={src} className="aspect-square overflow-hidden border border-slate-800">
                      <img src={src} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <div>
            <Reveal>
              <h1 data-testid="detail-title" className="font-display text-3xl font-semibold uppercase leading-tight tracking-wide text-white sm:text-4xl">
                {product.name[lang]}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                {t.detail.origin}: {t.detail.originValue}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <h2 className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-gold">{t.detail.overview}</h2>
              <p data-testid="detail-overview" className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">
                {product.overview[lang]}
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <h2 className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-gold">{t.detail.variants}</h2>
              <ul data-testid="detail-variants" className="mt-4 flex flex-col gap-2">
                {product.variants[lang].map((v) => (
                  <li key={v} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check size={14} className="mt-0.5 shrink-0 text-gold" /> {v}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.25}>
              <h2 className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-gold">{t.detail.specs}</h2>
              <div data-testid="detail-specs" className="mt-4">
                {product.specs.map((s, i) => (
                  <Row key={i} label={s.l[lang]} value={resolve(s.v)} />
                ))}
                {infoRows.slice(1).map((r) => (
                  <Row key={r.testid} label={r.label} value={r.value} testid={r.testid} />
                ))}
              </div>
            </Reveal>

            {product.coa && (
              <Reveal delay={0.3}>
                <a
                  data-testid="detail-coa-button"
                  href={product.coa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 border border-slate-600 px-6 py-3 font-mono text-[11px] tracking-[0.2em] text-slate-200 transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
                >
                  <FileSearch size={15} /> {t.detail.viewCoa.toUpperCase()}
                </a>
              </Reveal>
            )}

            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  data-testid="detail-quote-button"
                  to={`/?product=${product.slug}#quote`}
                  className="group flex items-center justify-center gap-2 bg-gold px-7 py-4 font-mono text-xs tracking-[0.2em] text-[#0B132B] transition-[box-shadow] duration-300 hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
                >
                  {t.detail.quote.toUpperCase()}
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  data-testid="detail-whatsapp-button"
                  href={waLink(t.wa.product(product.name.en))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 border border-slate-600 px-7 py-4 font-mono text-xs tracking-[0.2em] text-slate-200 transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
                >
                  <MessageCircle size={15} /> {t.detail.whatsapp.toUpperCase()}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-slate-600">
                {t.detail.note.toUpperCase()}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
